import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { BfiDataGrid, Column } from '../../common/SharedComponents';
import {
    ExperienceProjectTypeOptions,
    ExperienceProjectType,
    StructureTypeOptions,
    GridSettingType,
    LoanType,
    LoanStatusType,
} from '@apply/constants';
import { ExperienceApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { addMonths, formatCurrency } from '../../common/SharedFunctions';
import store, { experienceActions } from '@apply/adminStore';
import DateBox from 'devextreme-react/date-box';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { onCityCellRender, onStateCellRender, onBFIExperienceActionCellRender } from '../common/ExperienceFunctions';
import { useLocation } from 'react-router';

const BfiOutstandingFilter = ({ onChange }) => {
    const [hasRepayments, set_hasRepayments] = useState(false);
    const [fromDate, set_fromDate] = useState(addMonths(new Date(), -60));
    const [toDate, set_toDate] = useState('');
    const [typeFilterCore, set_typeFilterCore] = useState(true);
    const [typeFilterRental, set_typeFilterRental] = useState(true);
    const [typeFilterLOC, set_typeFilterLoc] = useState(true);

    const styles = {
        typeFilter: {
            display: 'inline-block',
            width: '80px',
            textAlign: 'left',
        },
        dateFilter: {
            display: 'inline-block',
            width: '180px',
            textAlign: 'left',
        },
        disabled: { color: 'silver' },
    };

    useEffect(() => {
        const coreTypes = [LoanType.Construction, LoanType.FixFlip, LoanType.BuildRent];
        const rentalTypes = [LoanType.Rental];
        const locTypes = [LoanType.SubNoteConstruction, LoanType.SubNoteFixFlip];
        let loanTypes = typeFilterCore ? coreTypes : [];
        if (typeFilterRental) loanTypes = [...loanTypes, ...rentalTypes];
        if (typeFilterLOC) loanTypes = [...loanTypes, ...locTypes];

        const filter = {
            hasRepayments,
            fromDate: fromDate ? new Date(fromDate).toLocaleDateString('en-us') : null,
            toDate: toDate ? new Date(toDate).toLocaleDateString('en-us') : null,
            loanTypes,
        };
        onChange && onChange(filter);
    }, [hasRepayments, fromDate, toDate, typeFilterCore, typeFilterRental, typeFilterLOC]);

    return (
        <div style={{ margin: '10px' }}>
            <span>
                <input
                    name='hasRepayments'
                    value='0'
                    checked={!hasRepayments}
                    type='radio'
                    onChange={(e) => set_hasRepayments(e.target.value === '1')}
                />
                &nbsp;No Repayments &nbsp;&nbsp;
                <input
                    name='hasRepayments'
                    value='1'
                    checked={hasRepayments}
                    type='radio'
                    onChange={(e) => set_hasRepayments(e.target.value === '1')}
                />
                &nbsp;Repayments&nbsp;&nbsp;
                <span style={!hasRepayments ? styles.disabled : null}>- From:&nbsp;</span>
                <span style={styles.dateFilter}>
                    <DateBox
                        applyValueMode='useButtons'
                        id='fromDate'
                        value={fromDate ? new Date(fromDate) : null}
                        max={new Date()}
                        onValueChange={set_fromDate}
                        disabled={!hasRepayments}
                    />
                </span>
                <span style={!hasRepayments ? styles.disabled : null}>To:&nbsp;</span>
                <span style={styles.dateFilter}>
                    <DateBox
                        applyValueMode='useButtons'
                        id='toDate'
                        value={toDate ? new Date(toDate) : null}
                        max={new Date()}
                        onValueChange={set_toDate}
                        disabled={!hasRepayments}
                    />
                </span>
                Loan Types:&nbsp;&nbsp;
                <span style={styles.typeFilter}>
                    <input
                        type='checkbox'
                        value={0}
                        checked={typeFilterCore}
                        onChange={(e) => set_typeFilterCore(e.target.checked)}
                    />{' '}
                    Core
                </span>
                <span style={styles.typeFilter}>
                    <input
                        type='checkbox'
                        value={1}
                        checked={typeFilterRental}
                        onChange={(e) => set_typeFilterRental(e.target.checked)}
                    />{' '}
                    Rental{' '}
                </span>
                <span style={styles.typeFilter}>
                    <input
                        type='checkbox'
                        checked={typeFilterLOC}
                        onChange={(e) => set_typeFilterLoc(e.target.checked)}
                    />{' '}
                    LOC
                </span>
            </span>
        </div>
    );
};

const BfiOutstandingTable = ({ principalId, gridStore }) => {
    const location = useLocation();
    const searchState = useSelector((state) => state.searchState);
    const { dispatch } = store;
    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);
    const [dataStore, set_dataStore] = useState(gridStore?.store);
    const [filter, set_filter] = useState(null);

    const onFilterChange = (fltr) => {
        set_filter(fltr);
    };

    const bindGrid = () => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.experience);
            set_dataStore(gridStore.store);
        }
    };

    const dataFunction = (criteria) => {
        if (!filter) {
            return;
        }
        criteria.hasRepayments = filter.hasRepayments;
        criteria.fromDate = filter.hasRepayments ? filter.fromDate : null;
        criteria.toDate = filter.hasRepayments ? filter.toDate : null;
        criteria.loanTypes = filter.loanTypes;
        return experienceApi.getExperiencesBfiOutstandingGrid(principalId, criteria);
    };

    useEffect(() => {
        bindGrid();
    }, [gridStore, filter]);

    const onRenderDate = (e) => {
        if (!e.value) {
            return null;
        }
        return new Date(e.value).toLocaleDateString('en-us');
    };

    const onRenderCurrency = (e) => {
        if (!e.value) {
            return null;
        }
        return formatCurrency(e.value, 0);
    };

    const onRenderCommitted = (e) => {
        let committedAmt = null;
        if (e.data.isPortfolio) {
            committedAmt = e.data.commitment + e.data.originationFee;
        } else {
            committedAmt = e.data.promissoryNote;
        }

        if (!committedAmt) {
            return null;
        }
        return formatCurrency(committedAmt, 0);
    };

    const onRoleRender = (e) => {
        if (e.value) {
            return '20% Plus Owner';
        } else {
            return 'Not 20% Owner';
        }
    };

    const onProjectTypeRender = (e) => {
        const option = ExperienceProjectTypeOptions.find((a) => a.id === e.value);
        if (!option) {
            return null;
        }
        if (option.id === ExperienceProjectType.FixFlipRehab) {
            if (e.data.isMajorRehab) {
                return `${option.adminName} (Major)`;
            } else {
                return option.adminName;
            }
        } else {
            return option.adminName;
        }
    };

    const onStructureTypeRender = (e) => {
        const option = StructureTypeOptions.find((a) => a.id === e.value);
        if (!option) {
            return null;
        } else {
            return option.shortName;
        }
    };

    const viewDetails = (event, id) => {
        event?.preventDefault();
        event?.stopPropagation();

        dispatch(
            experienceActions.setBfiDetailModal({
                bfiDetailModal: { isOpen: true, id },
            })
        );
    };

    if (!principalId || !dataStore) {
        return null;
    }

    return (
        <>
            <BfiOutstandingFilter onChange={onFilterChange} />
            <BfiDataGrid
                gridType={GridSettingType.ExperiencesBfiOutstandingTable}
                dataStore={dataStore}
                applicationInsights={applicationInsights}
                noDataText='No BFI-Outstanding Experience found for this Principal'
                onRowDblClick={(e) => viewDetails(null, e.data.id)}
            >
                <Column
                    dataField='projectTypeId'
                    caption='Project Type'
                    alignment='left'
                    cellRender={onProjectTypeRender}
                    width={200}
                />
                <Column
                    dataField='structureTypeId'
                    caption='Structure Type'
                    alignment='left'
                    cellRender={onStructureTypeRender}
                    width={150}
                />
                <Column
                    dataField='city'
                    caption='City'
                    width={150}
                    cellRender={onCityCellRender}
                />
                <Column
                    dataField='state'
                    caption='State'
                    width={50}
                    cellRender={onStateCellRender}
                />
                <Column
                    dataField='bfiNumber'
                    caption='BFI #'
                    width={125}
                />
                <Column
                    dataField='noteDate'
                    caption='Note Date'
                    width={100}
                    cellRender={onRenderDate}
                />
                <Column
                    dataField='monthsOutstanding'
                    caption='Months'
                    width={60}
                    alignment='left'
                />
                <Column
                    dataField='isTwentyPercentPlusOwner'
                    caption='Role'
                    alignment='left'
                    cellRender={onRoleRender}
                />
                <Column
                    caption='Committed'
                    width={100}
                    alignment='left'
                    cellRender={onRenderCommitted}
                />
                <Column
                    dataField='outstandingAmount'
                    caption='Outstanding'
                    width={100}
                    alignment='left'
                    cellRender={onRenderCurrency}
                />
                <Column
                    dataField='appraisedValue'
                    caption='Value'
                    width={100}
                    alignment='left'
                    cellRender={onRenderCurrency}
                />
                <Column
                    dataField='ltv'
                    caption='LTV'
                    width={100}
                    format={{ type: 'percent', precision: 1 }}
                    alignment='left'
                />
                <Column
                    dataField='appraisedDate'
                    caption='Value Date'
                    width={100}
                    cellRender={onRenderDate}
                />
                <Column
                    dataField='unitCount'
                    caption='Units'
                    width={60}
                    alignment='left'
                />
                <Column
                    dataField='credits'
                    caption='Crdts'
                    alignment='left'
                    width={50}
                />
                <Column
                    alignment='center'
                    cellRender={(e) => onBFIExperienceActionCellRender(e, location)}
                    width={50}
                    allowReordering={false}
                    allowSorting={false}
                    allowResizing={false}
                    type='actions'
                    fixed={true}
                    fixedPosition={'right'}
                />
            </BfiDataGrid>
        </>
    );
};

export default BfiOutstandingTable;
